import { PSSEData, PSSEBus, PSSELoad, PSSEFixedShunt, PSSEGenerator, PSSEBranch, PSSETransformer, BusType, ComponentStatus } from './psseTypes';

export function parsePSSERawFile(content: string): PSSEData {
  const lines = content.split('\n').map(line => line.trim());
  let currentIndex = 0;

  // Parse header (first 3 lines)
  const headerLine1 = lines[currentIndex++].split(',');
  const ic = parseInt(headerLine1[0]);
  const sbase = parseFloat(headerLine1[1]);
  const rev = parseInt(headerLine1[2]);
  const xfrrat = parseInt(headerLine1[3]);
  const nxfrat = parseInt(headerLine1[4]);
  const basfrq = parseFloat(headerLine1[5]);

  const title1 = lines[currentIndex++];
  const title2 = lines[currentIndex++];

  const header = { ic, sbase, rev, xfrrat, nxfrat, basfrq, title1, title2 };

  // Parse buses
  const buses: PSSEBus[] = [];
  while (currentIndex < lines.length && !lines[currentIndex].startsWith('0 /')) {
    const line = lines[currentIndex++];
    if (line.trim() === '') continue;
    
    const parts = line.split(',').map(p => p.trim());
    buses.push({
      number: parseInt(parts[0]),
      name: parts[1].replace(/'/g, ''),
      baseKV: parseFloat(parts[2]),
      busType: parseInt(parts[3]) as BusType,
      area: parseInt(parts[4]),
      zone: parseInt(parts[5]),
      owner: parseInt(parts[6]),
      voltageMagnitude: parseFloat(parts[7]),
      voltageAngle: parseFloat(parts[8])
    });
  }
  currentIndex++; // Skip "0 / END OF BUS DATA" line

  // Parse loads
  const loads: PSSELoad[] = [];
  while (currentIndex < lines.length && !lines[currentIndex].startsWith('0 /')) {
    const line = lines[currentIndex++];
    if (line.trim() === '') continue;
    
    const parts = line.split(',').map(p => p.trim());
    loads.push({
      busNumber: parseInt(parts[0]),
      loadId: parts[1].replace(/'/g, ''),
      status: parseInt(parts[2]) as ComponentStatus,
      area: parseInt(parts[3]),
      zone: parseInt(parts[4]),
      realPowerMW: parseFloat(parts[5]),
      reactivePowerMVAR: parseFloat(parts[6]),
      constantPowerReal: parseFloat(parts[7]),
      constantPowerReactive: parseFloat(parts[8]),
      constantCurrentReal: parseFloat(parts[9]),
      constantCurrentReactive: parseFloat(parts[10]),
      owner: parseInt(parts[11]),
      scale: parseInt(parts[12])
    });
  }
  currentIndex++; // Skip "0 / END OF LOAD DATA" line

  // Parse fixed shunt data
  // Format: I, ID, STATUS, GL, BL  (GL/BL are MW/Mvar at 1 pu voltage)
  const fixedShunts: PSSEFixedShunt[] = [];
  while (currentIndex < lines.length && !lines[currentIndex].startsWith('0 /')) {
    const line = lines[currentIndex++];
    if (line.trim() === '') continue;

    const parts = line.split(',').map(p => p.trim());
    fixedShunts.push({
      busNumber: parseInt(parts[0]),
      shuntId: parts[1].replace(/'/g, ''),
      status: parseInt(parts[2]) as ComponentStatus,
      conductanceMW: parseFloat(parts[3]),
      susceptanceMVAR: parseFloat(parts[4])
    });
  }
  currentIndex++; // Skip "0 / END OF FIXED SHUNT DATA" line

  // Parse generators
  const generators: PSSEGenerator[] = [];
  while (currentIndex < lines.length && !lines[currentIndex].startsWith('0 /')) {
    const line = lines[currentIndex++];
    if (line.trim() === '') continue;
    
    const parts = line.split(',').map(p => p.trim());
    generators.push({
      busNumber: parseInt(parts[0]),
      generatorId: parts[1].replace(/'/g, ''),
      realPowerMW: parseFloat(parts[2]),
      reactivePowerMVAR: parseFloat(parts[3]),
      maxReactiveMVAR: parseFloat(parts[4]),
      minReactiveMVAR: parseFloat(parts[5]),
      voltageMagnitude: parseFloat(parts[6]),
      remoteBusNumber: parseInt(parts[7]),
      maxRealPowerMW: parseFloat(parts[8]),
      minRealPowerMW: parseFloat(parts[9]),
      machineBaseKVA: parseFloat(parts[10]),
      impedanceReal: parseFloat(parts[11]),
      impedanceReactive: parseFloat(parts[12]),
      status: parseInt(parts[13]) as ComponentStatus,
      ratedRealPowerMW: parseFloat(parts[14])
    });
  }
  currentIndex++; // Skip "0 / END OF GENERATOR DATA" line

  // Parse branches
  const branches: PSSEBranch[] = [];
  while (currentIndex < lines.length && !lines[currentIndex].startsWith('0 /')) {
    const line = lines[currentIndex++];
    if (line.trim() === '') continue;
    
    const parts = line.split(',').map(p => p.trim());
    branches.push({
      fromBus: parseInt(parts[0]),
      toBus: parseInt(parts[1]),
      circuitId: parts[2].replace(/'/g, ''),
      resistance: parseFloat(parts[3]),
      reactance: parseFloat(parts[4]),
      chargingSusceptance: parseFloat(parts[5]),
      rateA: parseFloat(parts[6]),
      rateB: parseFloat(parts[7]),
      rateC: parseFloat(parts[8]),
      tapRatio: parseFloat(parts[9]),
      phaseShift: parseFloat(parts[10]),
      fromBusShunt: parseFloat(parts[11]),
      toBusShunt: parseFloat(parts[12]),
      status: parseInt(parts[13]) as ComponentStatus
    });
  }
  currentIndex++; // Skip "0 / END OF BRANCH DATA" line

  // Parse transformers. A 2-winding transformer is a 4-line record:
  //   1: I, J, K, CKT, CW, CZ, CM, MAG1, MAG2, NMETR, 'NAME', STAT, ...
  //   2: R1-2, X1-2, SBASE1-2      (series impedance; pu on system base when CZ=1)
  //   3: WINDV1, NOMV1, ANG1, ...  (off-nominal turns ratio / phase shift on winding 1)
  //   4: WINDV2, NOMV2
  const transformers: PSSETransformer[] = [];
  while (currentIndex < lines.length && !lines[currentIndex].startsWith('0 /')) {
    if (lines[currentIndex].startsWith('Q')) break; // End of file marker
    if (lines[currentIndex].trim() === '') {
      currentIndex++;
      continue;
    }

    const header = lines[currentIndex].split(',').map(p => p.trim());
    if (header.length < 8) break; // Not a transformer header — stop.

    // Guard against a truncated record at end of section.
    if (currentIndex + 3 >= lines.length) break;

    const impedance = lines[currentIndex + 1].split(',').map(p => p.trim());
    const winding1 = lines[currentIndex + 2].split(',').map(p => p.trim());

    transformers.push({
      fromBus: parseInt(header[0]),
      toBus: parseInt(header[1]),
      tertiaryBus: parseInt(header[2]),
      circuitId: header[3].replace(/'/g, ''),
      windingDataI: parseInt(header[4]),
      windingDataJ: parseInt(header[5]),
      windingDataK: parseInt(header[6]),
      magnetizingAdmittanceG: parseFloat(header[7]),
      magnetizingAdmittanceB: parseFloat(header[8]),
      windingCount: 2,
      resistance: parseFloat(impedance[0]),
      reactance: parseFloat(impedance[1]),
      baseMVA: parseFloat(impedance[2]) || 100,
      tapRatio: parseFloat(winding1[0]) || 1.0,
      phaseShift: parseFloat(winding1[2]) || 0
    });

    currentIndex += 4; // Advance past the 4-line record.
  }

  return {
    header,
    buses,
    loads,
    fixedShunts,
    generators,
    branches,
    transformers
  };
}